import { useState, useEffect } from "react";
import {
  Control,
  useFieldArray,
  FieldValues,
  ArrayPath,
} from "react-hook-form";

interface AgencyFragment {
  id: string;
  name?: string | null;
  streetAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  city?: string | null;
  state?: string | null;
  county?: string | null;
  mailingAddress?: string | null;
}

// Helper function to safely extract agency from nested structure
function extractAgency<T extends AgencyFragment>(
  item: any,
  agencyKey: string
): T | null {
  if (!item || typeof item !== "object") return null;
  const agency = item[agencyKey];
  if (!agency || typeof agency !== "object" || !agency.id) return null;
  return agency;
}

interface UseSupplementalAgenciesProps<
  T extends AgencyFragment,
  TFieldValues extends FieldValues = any
> {
  control: Control<TFieldValues>;
  fieldName: ArrayPath<TFieldValues>;
  existingAgencies?: Array<any> | null;
  agencyKey: string; // "fireDepartment" or "lepc"
  defaultFieldValue?: string; // Default value for new form fields
}

export function useSupplementalAgencies<
  T extends AgencyFragment,
  TFieldValues extends FieldValues = any
>({
  control,
  fieldName,
  existingAgencies = [],
  agencyKey,
  defaultFieldValue = "",
}: UseSupplementalAgenciesProps<T, TFieldValues>) {
  const {
    fields: supplementalFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: fieldName,
  });

  // Local state for supplemental agency selections (for display purposes)
  const [supplementalSelections, setSupplementalSelections] = useState<
    (T | null)[]
  >([]);

  // Initialize selections from GraphQL data when it becomes available
  useEffect(() => {
    if (
      existingAgencies &&
      existingAgencies.length > 0 &&
      supplementalSelections.length === 0 &&
      supplementalFields.length > 0
    ) {
      const selections = existingAgencies
        .map((item) => extractAgency<T>(item, agencyKey))
        .filter((agency): agency is T => agency !== null);
      setSupplementalSelections(selections);
    }
  }, [
    existingAgencies,
    supplementalSelections.length,
    supplementalFields.length,
    agencyKey,
  ]);

  const handleSelectionChange = (index: number, agency: T | null) => {
    const newSelections = [...supplementalSelections];
    newSelections[index] = agency;
    setSupplementalSelections(newSelections);
  };

  const handleAdd = () => {
    // NOTE: Type assertion required here due to react-hook-form limitation
    // useFieldArray is designed for object arrays (e.g., {id: string, value: string}[])
    // but our form field stores primitive strings (supplementalLepcIds: string[])
    // The cast is safe because append() will correctly add a string to the string[] field
    append(defaultFieldValue as any);
    // Add null to display selections (for picker state)
    setSupplementalSelections([...supplementalSelections, null]);
  };

  const handleRemove = (index: number) => {
    remove(index);
    const newSelections = [...supplementalSelections];
    newSelections.splice(index, 1);
    setSupplementalSelections(newSelections);
  };

  return {
    supplementalFields,
    supplementalSelections,
    handleSelectionChange,
    handleAdd,
    handleRemove,
  };
}
